import React, { Suspense } from 'react';
import dynamic from 'next/dynamic';
import ErrorBoundary from 'components/ErrorBoundary';

import type { ComponentType, PropsWithChildren } from 'react';

import type { PolarisProvider as PolarisProviderType } from '@buoy-components/polaris/types';

/**
 * This higher-order component wraps a given component with the PolarisProvider.
 * PolarisProvider is dynamically imported.
 *
 * While the PolarisProvider is loading, the wrapped component is displayed.
 * Once the PolarisProvider is loaded, the wrapped component is displayed within the PolarisProvider.
 * If the PolarisProvider fails to load, the wrapped component is displayed.
 */

function withPolarisProvider<Props>(
  Component: ComponentType<PropsWithChildren<Props>>,
) {
  return function WithPolarisProvider(props: PropsWithChildren<Props>) {
    const PolarisProvider = dynamic(
      () =>
        (
          import('polaris/PolarisProvider') as Promise<PolarisProviderType>
        ).then((component: PolarisProviderType) => {
          // The module federation plugin returns an empty component with the name: 'pg', when the
          // remote is offline.
          if ((component as any)?.default?.name === 'pg') {
            throw new Error('Polaris is offline.');
          }

          return component;
        }),
      {
        loading: () => <Component {...props} />,
        ssr: false,
      },
    );

    return (
      <Suspense fallback={<Component {...props} />}>
        <ErrorBoundary fallback={<Component {...props} />}>
          <PolarisProvider>
            <Component {...props} />
          </PolarisProvider>
        </ErrorBoundary>
      </Suspense>
    );
  };
}

export default withPolarisProvider;
